import { supabase } from '@/lib/supabase';
import { removeMedia, uploadProjectMedia, fileTypeFromMime } from '@/lib/storage';
import type { Database, FileRow } from '@/types/database';

type FileUpdate = Database['public']['Tables']['files']['Update'];

export async function listFiles(projectId: string): Promise<FileRow[]> {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function uploadAndRecordFile(
  file: File,
  projectId: string,
  roomId: string | null,
): Promise<FileRow> {
  const { path, mimeType, sizeBytes } = await uploadProjectMedia(file, projectId, 'files');
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('files')
    .insert({
      project_id: projectId,
      room_id: roomId,
      name: file.name,
      file_type: fileTypeFromMime(mimeType),
      storage_path: path,
      size_bytes: sizeBytes,
      mime_type: mimeType,
      uploaded_by: user?.id ?? null,
    })
    .select()
    .single();
  if (error) {
    // Best-effort: remove the orphaned object so storage stays clean.
    await removeMedia(path).catch(() => {});
    throw error;
  }
  return data;
}

export async function updateFile(id: string, patch: FileUpdate): Promise<FileRow> {
  const { data, error } = await supabase
    .from('files')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFileRecord(file: FileRow): Promise<void> {
  // Delete the storage object first; if it succeeds, drop the row.
  await removeMedia(file.storage_path);
  const { error } = await supabase.from('files').delete().eq('id', file.id);
  if (error) throw error;
}
